import { useContext, useEffect } from "react";
import { Col } from "react-grid-system";

import { BaseLayout } from "components/base-layout";
import { Stack } from "components/common/stack";
import { Coverage, Details } from "components/renderers";
import { EasyPayCard } from "components/shared/easypay-card";
import { FaqSidebar } from "components/shared/faq-sidebar";
import { InsuranceRepresentative } from "components/shared/insurance-representative";
import { MainLayout } from "components/shared/main-layout";
import { type NotificationName } from "components/shared/notifications/notifications";
import { PaperlessCard } from "components/shared/paperless-card";
import { type KeystoneOfferEligibility } from "data/keystone-api-mapping";
import { usePolicyDetail } from "data/ss-data-hooks";
import { FeaturedOffers } from "pages/overview/featured-offers/featured-offers";
import { useFeaturedOffers } from "pages/overview/featured-offers/hooks";
import { WelcomeBanner } from "pages/overview/welcome-banner";
import { isEasyPayEligible, scrollToYPosition, trackEvent } from "utils";

import { CoverageContext } from "./extended-coverages/hooks";
import CoverageUpdate from "./extended-coverages/update-coverage";
import { useError } from "./extended-coverages/update-coverage/hooks";
import styles from "./overview.module.scss";

const AVAILABLE_NOTIFICATIONS: NotificationName[] = [
  "Coverage",
  "CancellationPayment",
];

const Overview = () => {
  const { data } = usePolicyDetail();

  const { featuredOffers, isLoading: isOffersLoading } = useFeaturedOffers();

  const { coveragePage, setCoveragePage } = useContext(CoverageContext);
  const errorState = useError();

  useEffect(() => {
    if (coveragePage.scroll_to) scrollToYPosition(coveragePage.scroll_to);
  }, [coveragePage]);

  useEffect(() => {
    trackEvent("Overview");
  }, []);

  if (!data) {
    return <BaseLayout loading />;
  }

  if (coveragePage.open) {
    return (
      <BaseLayout hideNavigation hidePolicyHeader>
        <CoverageUpdate
          error={errorState.error}
          dispatchError={errorState.dispatchError}
          setCoveragePage={setCoveragePage}
        />
      </BaseLayout>
    );
  }

  const showAffiliateBlog =
    !isOffersLoading && data?.userPolicyProperties.policyType == "HO3";

  return (
    <BaseLayout availableNotifications={AVAILABLE_NOTIFICATIONS}>
      <MainLayout
        sidebar={
          <Sidebar
            easyPayEligible={isEasyPayEligible(
              data?.userBilling,
              data?.userDetails,
            )}
          />
        }
        topRow={
          <TopRow
            showAffiliateBlog={showAffiliateBlog}
            featuredOffers={featuredOffers}
          />
        }
        splitTopRow
      >
        <Main />
      </MainLayout>
    </BaseLayout>
  );
};

export default Overview;

function TopRow({
  showAffiliateBlog,
  featuredOffers,
}: {
  showAffiliateBlog: boolean;
  featuredOffers: KeystoneOfferEligibility[];
}) {
  const isTwoCol = featuredOffers.length || showAffiliateBlog;
  return (
    <>
      <Col lg={isTwoCol ? 6 : 12}>
        <WelcomeBanner />
      </Col>
      {isTwoCol ? (
        <Col lg={6} className={styles.featuredOffers}>
          <FeaturedOffers offers={featuredOffers} />
        </Col>
      ) : null}
    </>
  );
}

function Sidebar({ easyPayEligible }: { easyPayEligible: boolean }) {
  return (
    <Stack>
      {easyPayEligible && <EasyPayCard />}
      <InsuranceRepresentative />
      <FaqSidebar page="overview" />
      <PaperlessCard pageName="Overview" />
    </Stack>
  );
}

function Main() {
  return (
    <Stack>
      <Details />
      <Coverage />
    </Stack>
  );
}
